import { useEffect, useRef, useState } from 'react';
import { useMeeting } from '../meeting/meetingProviderContextDef';

/**
 *
 * @param {string} key
 * @param {{
 *  onValueChanged?: Function,
 * }} options
 * @returns {{
 *  setValue: (value : string) => void
 *  getValue: () => string
 * }}
 */
const useRealtimeStore = (key, { onValueChanged = () => {} } = {}) => {
  const observerIdRef = useRef(null);
  const { meeting, isMeetingJoined } = useMeeting();
  const isObserving = useRef(false);

  /**
   * Set or update a key-value pair in the RealtimeStore.
   *
   * @async
   * @param {String|null} value - The value to set (string only). Pass `null` to delete the key.
   * @returns {Promise<void>}
   *
   * @throws {Error} If value is not a string or exceeds 1 KB.
   * @throws {Error} If request fails.
   */
  const setValue = async value => {
    if (meeting) {
      return await meeting.realtimeStore.setValue(key, value);
    }
  };

  /**
   * Retrieve the value associated with a key from the RealtimeStore.
   *
   * @async
   * @returns {Promise<String|null>} - Returns the stored value from the RealtimeStore..
   *
   * @throws {Error} If key doesn’t exist or request fails.
   */
  const getValue = async () => {
    if (meeting) {
      return await meeting.realtimeStore.getValue(key);
    }
  };

  const _handleRealtimeStore = data => {
    onValueChanged(data);
  };

  const subscribeToKey = async meeting => {
    const observer = await meeting?.realtimeStore?.observe(
      key,
      _handleRealtimeStore
    );
    if (observer) {
      observerIdRef.current = observer;
    }
  };

  useEffect(() => {
    if (meeting && !isObserving.current && isMeetingJoined && onValueChanged) {
      isObserving.current = true;
      subscribeToKey(meeting);

      return () => {
        if (observerIdRef.current) {
          meeting?.realtimeStore?.stopObserving(observerIdRef.current);
          observerIdRef.current = null;
        }
        isObserving.current = false;
      };
    }
  }, [meeting, isMeetingJoined]);

  return { setValue, getValue };
};

export default useRealtimeStore;
